import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Sheet } from '../components/Sheet.jsx';
import { Icon } from '../components/Icon.jsx';
import { api } from '../lib/api.js';
import { dokKeys, ANSEHEN_MS } from '../hooks/data.js';

const enc = encodeURIComponent;

/**
 * «Aus früherem Protokoll übernehmen»: Formulare dieses Auftrags – oder eines anderen (periodische Kontrolle ist meist
 * ein neuer Auftrag) – auswählen; das gewählte wird geladen (aus dem Gerät, wenn es schon offen war) und übergeben.
 * @param {{ v: object, art: string, nr: string, dateiId: string, onWahl: (daten: object, titel: string) => void, onClose: () => void }} p
 */
export function UebernehmenSheet({ v, art, nr, dateiId, onWahl, onClose }) {
  const qc = useQueryClient();
  const [auftrag, setAuftrag] = useState(nr);
  const [eingabe, setEingabe] = useState('');
  const [laedt, setLaedt] = useState(null);
  const [fehler, setFehler] = useState('');
  const [listeKey, dokKey] = dokKeys(art);
  const q = useQuery({ queryKey: [listeKey, auftrag], queryFn: () => api(`/auftraege/${enc(auftrag)}/${art}`), staleTime: ANSEHEN_MS, retry: false });
  const listen = (q.data?.listen ?? []).filter((l) => !(auftrag === nr && l.dateiId === dateiId)).slice().reverse(); // neueste zuerst

  const waehlen = async (l) => {
    setFehler('');
    setLaedt(l.dateiId);
    try {
      const r = await qc.fetchQuery({ queryKey: [dokKey, auftrag, l.dateiId], queryFn: () => api(`/auftraege/${enc(auftrag)}/${art}/${enc(l.dateiId)}`), staleTime: ANSEHEN_MS });
      onWahl(r.daten, l.titel);
    } catch (err) {
      setFehler(err.message);
    } finally {
      setLaedt(null);
    }
  };

  return (
    <Sheet title={v.uebernehmen.titel} onClose={onClose}>
      <p className="sub" style={{ fontSize: 14 }}>{v.uebernehmen.hinweis}</p>
      <form className="row" style={{ gap: 8 }} onSubmit={(e) => { e.preventDefault(); if (eingabe.trim()) setAuftrag(eingabe.trim()); }}>
        <input className="input" style={{ flex: 1 }} inputMode="numeric" value={eingabe} onChange={(e) => setEingabe(e.target.value)} placeholder={`Anderer Auftrag (jetzt: ${auftrag})`} aria-label="Auftragsnummer" />
        <button type="submit" className="btn secondary" disabled={!eingabe.trim()}>Suchen</button>
      </form>
      {auftrag !== nr && <button type="button" className="linkbtn" style={{ alignSelf: 'flex-start' }} onClick={() => { setAuftrag(nr); setEingabe(''); }}>Zurück zu diesem Auftrag</button>}
      {fehler && <div className="notice err" role="alert">{fehler}</div>}
      {q.isError && !q.data && <div className="notice err" role="alert">{q.error.message}</div>}
      {!q.data && !q.isError && <div className="skeleton" style={{ height: 96 }} />}
      {q.data && !listen.length && <div className="empty">Keine {v.mehrzahl} in Auftrag {auftrag}.</div>}
      {listen.length > 0 && (
        <section className="card list" style={{ padding: '4px 16px' }} aria-label={`${v.mehrzahl} in Auftrag ${auftrag}`}>
          {listen.map((l) => (
            <button key={l.dateiId} type="button" className="listrow plainrow" disabled={!!laedt} onClick={() => waehlen(l)} style={{ cursor: 'pointer' }}>
              <Icon name={v.icon} size={20} />
              <span className="stack" style={{ gap: 0, flex: 1, minWidth: 0 }}>
                <span className="ellipsis" style={{ fontWeight: 800 }}>{l.titel}</span>
                <span className="sub ellipsis" style={{ fontSize: 12 }}>{laedt === l.dateiId ? 'Lädt …' : l.name}</span>
              </span>
              <Icon name="chevron" size={18} />
            </button>
          ))}
        </section>
      )}
    </Sheet>
  );
}
